import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import {
  Search,
  ChevronDown,
  CheckCircle,
  Crown,
  Building2,
  LayoutGrid,
  List,
  MapPin,
  Calendar,
  Users,
  DollarSign,
  PartyPopper,
  X,
  Briefcase,
  Clock,
  User,
  Globe,
  Award,
  FileBadge,
  GraduationCap,
  Plus,
  Dumbbell,
  Shapes,
  ArrowLeft,
  Lock,
  ArrowUp,
  UserCheck,
  MessageCircle,
  ClipboardList,
  MapPinned,
  UsersRound,
  MousePointerClick,
  Send,
  Repeat2,
  CalendarClock,
} from 'lucide-react';
import { useRef, useCallback } from 'react';
import ClassCard from '../components/ClassCard';
import ClassCategoryView, { PopularClassGrid, type CategoryNode } from '../components/class/ClassCategoryView';
import CertificateWallet from '../components/certificate/CertificateWallet';
import WalletIntro from '../components/WalletIntro';
import DigitalBadge from '../components/DigitalBadge';
import KnowledgeChallenge from '../components/KnowledgeChallenge';
import InstructorDetailsModal from '../components/InstructorDetailsModal';
import MainTabsNav from '../components/MainTabsNav';
import FeaturedProviders from '../components/FeaturedProviders';
import SectionHeading from '../components/SectionHeading';
import { useAuthStore } from '../stores/authStore';
import { getPositionLabel } from '../utils/jobPositions';
import JobPositionBoard from '../components/JobPositionBoard';
import StartChatButton from '../components/StartChatButton';
import { getAllProvinces, getDistricts } from '../data/regions';
import ResumeTemplate1Preview from '../components/ResumeTemplate1Preview';
import ResumeTemplate2Preview from '../components/ResumeTemplate2Preview';
import ResumeTemplate3Preview from '../components/ResumeTemplate3Preview';
import ResumeModalScaler from '../components/ResumeModalScaler';

interface PremiumProvider {
  id: string;
  email: string;
  company_name: string | null;
  ceo_name: string | null;
  avatar_url: string | null;
  banner_image_url: string | null;
  premium_started_at: string | null;
  address: string | null;
  business_category: string | null;
  tagline: string | null;
}

interface ClassItem {
  id: string;
  title: string;
  provider_id: string;
  provider_name: string;
  thumbnail_url: string | null;
  price: number;
  discount_price: number;
  start_date: string;
  end_date: string;
  location_type: 'ONLINE' | 'OFFLINE' | 'HYBRID';
  location_address: string | null;
  location_province?: string | null;
  location_district?: string | null;
  class_type: 'CERTIFICATE' | 'COMPLETION' | 'WORKSHOP' | 'OTHER';
  status: 'RECRUITING' | 'IN_PROGRESS' | 'COMPLETED' | 'CLOSED';
  capacity: number;
  current_count: number;
  provider_business_category: string | null;
  created_at: string;
  closing_soon: boolean;
  favorites_count?: number;
  is_premium?: boolean;
  category_path?: { id: string; name: string; level: number }[];
  instructors?: {
    id: string;
    name: string;
    description: string;
    image_url: string | null;
  }[];
}

const categories = ['전체', '필라테스', '요가', 'PT/트레이닝', '그룹피트니스', '재활/마사지'];

type ClassTabKey = 'main' | 'certificate' | 'online' | 'completion' | 'workshop' | 'badge' | 'wallet';

const GOOGLE_MAPS_API_KEY = 'AIzaSyCoR825_R9dtOug9dolmdrXmvKdSHplvg0';

function JobMap({ jobs, centerAddress, onSelectJob }: { jobs: any[]; centerAddress: string; onSelectJob: (job: any) => void }) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const onSelectJobRef = useRef(onSelectJob);
  onSelectJobRef.current = onSelectJob;

  const initMap = useCallback(() => {
    if (!mapRef.current || !(window as any).google?.maps) return;

    const google = (window as any).google;

    // Find center from jobs with coordinates, or default to Seoul
    // 공고별 근무지 좌표 우선, 없으면 업체 좌표 fallback
    const getJobLat = (j: any) => j.work_location_lat ?? j.employer_lat;
    const getJobLng = (j: any) => j.work_location_lng ?? j.employer_lng;
    const jobsWithCoords = jobs.filter((j: any) => getJobLat(j) && getJobLng(j));
    const defaultCenter = { lat: 37.5665, lng: 126.9780 }; // Seoul

    let center = defaultCenter;
    if (jobsWithCoords.length > 0) {
      center = { lat: getJobLat(jobsWithCoords[0]), lng: getJobLng(jobsWithCoords[0]) };
    }

    const map = new google.maps.Map(mapRef.current, {
      center,
      zoom: 12,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
    });
    mapInstanceRef.current = map;

    // Add markers
    const empTypes: Record<string, string> = { FULL_TIME: '정규직', CONTRACT: '계약직', PART_TIME: '파트타임', FREELANCE: '프리랜서' };

    jobsWithCoords.forEach((job: any) => {
      const marker = new google.maps.Marker({
        position: { lat: getJobLat(job), lng: getJobLng(job) },
        map,
        title: job.title,
        icon: job.is_premium_employer
          ? { url: 'https://maps.google.com/mapfiles/ms/icons/gold-dot.png' }
          : undefined,
      });

      const displayAddress = job.work_location || job.employer_address;
      const infoId = `job-info-${job.id}`;
      const infoWindow = new google.maps.InfoWindow({
        content: `
          <div style="max-width:240px;font-family:sans-serif;cursor:pointer;" id="${infoId}">
            ${job.is_premium_employer ? '<div style="color:#d97706;font-size:11px;font-weight:bold;margin-bottom:2px;">⭐ 프리미엄</div>' : ''}
            <div style="font-weight:bold;font-size:14px;margin-bottom:4px;">${job.title}</div>
            <div style="font-size:12px;color:#666;">${job.company_name || ''}</div>
            <div style="font-size:12px;color:#666;margin-top:2px;">${getPositionLabel(job.position)} · ${empTypes[job.employment_type] || ''}</div>
            ${displayAddress ? `<div style="font-size:11px;color:#999;margin-top:4px;">📍 ${displayAddress}</div>` : ''}
            <div style="margin-top:8px;text-align:center;">
              <span style="display:inline-block;background:#1E3A5F;color:#fff;font-size:12px;padding:4px 14px;border-radius:6px;font-weight:500;">상세보기</span>
            </div>
          </div>
        `,
      });

      marker.addListener('click', () => {
        infoWindow.open(map, marker);
      });

      google.maps.event.addListener(infoWindow, 'domready', () => {
        const el = document.getElementById(infoId);
        if (el) {
          el.addEventListener('click', () => {
            onSelectJobRef.current(job);
            infoWindow.close();
          });
        }
      });
    });

    // Fit bounds if multiple markers
    if (jobsWithCoords.length > 1) {
      const bounds = new google.maps.LatLngBounds();
      jobsWithCoords.forEach((job: any) => {
        bounds.extend({ lat: getJobLat(job), lng: getJobLng(job) });
      });
      map.fitBounds(bounds);
    }
  }, [jobs]);

  useEffect(() => {
    // Load Google Maps JS API
    if ((window as any).google?.maps) {
      initMap();
      return;
    }

    const existingScript = document.querySelector(`script[src*="maps.googleapis.com"]`);
    if (existingScript) {
      existingScript.addEventListener('load', initMap);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&language=ko`;
    script.async = true;
    script.defer = true;
    script.onload = initMap;
    document.head.appendChild(script);
  }, [initMap]);

  // Re-init when jobs change
  useEffect(() => {
    if ((window as any).google?.maps && mapRef.current) {
      initMap();
    }
  }, [jobs, initMap]);

  return (
    <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm">
      <div ref={mapRef} style={{ width: '100%', height: '350px' }} />
    </div>
  );
}

// 구인구직 — 대강을 제외한 포지션별 전용 서브페이지(?view=trainer 등). 대강은 기존 substitute 뷰 유지
type PositionSubviewKey = 'trainer' | 'pilates' | 'other' | 'yoga' | 'academy';
type PositionIconType = 'dumbbell' | 'pilates' | 'yoga' | 'shapes' | 'academy';

const POSITION_SUBVIEWS: Record<PositionSubviewKey, {
  value: string;
  label: string;
  subtext?: string;
  description: string;
  icon: PositionIconType;
  color: string;   // 아이콘/포인트 컬러
  bgFrom: string;  // 아이콘 네비 카드 그라디언트 시작색
  border: string;  // 카드 테두리
  soft: string;    // 배지/히어로 배경(연한 톤)
}> = {
  trainer: {
    value: '트레이너', label: '트레이너',
    description: '헬스장·PT샵·크로스핏 등에서 활동할 트레이너 채용공고를 확인하세요.',
    icon: 'dumbbell', color: '#2F6FB5', bgFrom: '#F3F8FF', border: '#CFE1F7', soft: '#EBF2FC',
  },
  pilates: {
    value: '필라테스 강사', label: '필라테스 강사', subtext: '(필라테스, 자이로토닉, 바레)',
    description: '필라테스·자이로토닉·바레 스튜디오의 강사 채용공고를 확인하세요.',
    icon: 'pilates', color: '#6D5AA8', bgFrom: '#F5F3FF', border: '#DDD6FE', soft: '#F1EEFC',
  },
  yoga: {
    value: '요가 강사', label: '요가 강사',
    description: '요가 스튜디오·센터의 요가 강사 채용공고를 확인하세요.',
    icon: 'yoga', color: '#2F8A6E', bgFrom: '#F2FBF7', border: '#CDEEDF', soft: '#E9F8F1',
  },
  other: {
    value: '기타 종목', label: '기타 종목', subtext: '(골프, 수영, FC, 댄스, GX, 구기 등)',
    description: '골프·수영·축구클럽·댄스·GX·구기 등 다양한 종목의 채용공고를 확인하세요.',
    icon: 'shapes', color: '#C7782F', bgFrom: '#FFF8F1', border: '#F7D9B7', soft: '#FDEFDE',
  },
  academy: {
    value: '아카데미 교육 강사', label: '아카데미 교육 강사',
    description: '자격증 과정 등 지도자 양성 교육을 담당할 교육 강사 채용공고를 확인하세요.',
    icon: 'academy', color: '#B85D77', bgFrom: '#FFF4F7', border: '#F2D0DA', soft: '#FBE7ED',
  },
};
const POSITION_SUBVIEW_KEYS = Object.keys(POSITION_SUBVIEWS) as PositionSubviewKey[];

// 포지션 아이콘 — 트레이너/기타 종목/아카데미는 lucide, 필라테스/요가는 목업의 커스텀 SVG 그대로 이식
function PositionIcon({ type, className }: { type: PositionIconType; className?: string }) {
  if (type === 'dumbbell') return <Dumbbell className={className} />;
  if (type === 'shapes') return <Shapes className={className} />;
  if (type === 'academy') return <GraduationCap className={className} />;
  if (type === 'pilates') {
    return (
      <svg className={className} viewBox="0 0 64 64" aria-label="필라테스 바렐" role="img">
        <path fill="currentColor" d="M11 43V26.5C11 17.9 17.9 11 26.5 11S42 17.9 42 26.5V43H11Z" />
        <path d="M8 47H55V12" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  return (
    <svg className={className} viewBox="0 0 883 988" aria-label="요가 명상 자세" role="img" preserveAspectRatio="xMidYMid meet">
      <path d="M 0.0 748.0 L 2.0 765.0 L 16.0 775.0 L 106.0 773.0 L 225.0 680.0 L 257.0 631.0 L 290.0 477.0 L 293.0 628.0 L 275.0 721.0 L 245.0 751.0 L 156.0 777.0 L 235.0 796.0 L 477.0 881.0 L 739.0 781.0 L 640.0 752.0 L 607.0 719.0 L 590.0 627.0 L 593.0 478.0 L 626.0 631.0 L 658.0 680.0 L 777.0 773.0 L 867.0 775.0 L 883.0 759.0 L 874.0 734.0 L 816.0 715.0 L 713.0 608.0 L 670.0 374.0 L 649.0 325.0 L 590.0 288.0 L 488.0 279.0 L 317.0 283.0 L 245.0 313.0 L 216.0 362.0 L 171.0 606.0 L 67.0 715.0 L 19.0 728.0 Z" fill="currentColor" />
      <path d="M 16.0 848.0 L 14.0 873.0 L 19.0 894.0 L 30.0 914.0 L 44.0 929.0 L 84.0 952.0 L 145.0 970.0 L 203.0 979.0 L 277.0 985.0 L 484.0 988.0 L 547.0 986.0 L 562.0 976.0 L 568.0 963.0 L 568.0 948.0 L 564.0 937.0 L 553.0 925.0 L 539.0 917.0 L 433.0 886.0 L 227.0 809.0 L 165.0 791.0 L 132.0 786.0 L 97.0 786.0 L 66.0 793.0 L 53.0 799.0 L 34.0 814.0 L 25.0 826.0 Z" fill="currentColor" />
      <path d="M 427.0 0.0 L 404.0 5.0 L 378.0 17.0 L 364.0 27.0 L 347.0 44.0 L 333.0 65.0 L 324.0 87.0 L 319.0 113.0 L 319.0 133.0 L 324.0 159.0 L 335.0 184.0 L 348.0 202.0 L 362.0 216.0 L 382.0 230.0 L 405.0 240.0 L 429.0 245.0 L 454.0 245.0 L 475.0 241.0 L 503.0 229.0 L 521.0 216.0 L 539.0 197.0 L 549.0 182.0 L 559.0 158.0 L 564.0 129.0 L 563.0 106.0 L 558.0 84.0 L 547.0 60.0 L 535.0 43.0 L 515.0 24.0 L 498.0 13.0 L 476.0 4.0 L 456.0 0.0 Z" fill="currentColor" />
      <path d="M 869.0 863.0 L 864.0 838.0 L 854.0 820.0 L 844.0 809.0 L 832.0 800.0 L 811.0 791.0 L 798.0 788.0 L 776.0 787.0 L 744.0 792.0 L 706.0 804.0 L 556.0 868.0 L 491.0 893.0 L 546.0 909.0 L 560.0 917.0 L 572.0 929.0 L 579.0 944.0 L 580.0 962.0 L 577.0 973.0 L 570.0 984.0 L 663.0 980.0 L 709.0 975.0 L 753.0 967.0 L 791.0 956.0 L 820.0 943.0 L 835.0 933.0 L 857.0 908.0 L 865.0 890.0 Z" fill="currentColor" />
    </svg>
  );
}

// 채용 서비스 이용 안내 — 구인구직 메인(1열)·포지션별 서브페이지(4열) 공용
function JobServiceGuide({
  columns = 1, title = '채용 서비스 이용 안내', firstItem,
}: {
  columns?: 1 | 4;
  title?: string;
  firstItem?: { title: any; desc: any };
}) {
  const items: { icon: typeof Search; color: string; bg: string; title: any; desc: any }[] = [
    {
      icon: Search, color: 'text-blue-600', bg: 'bg-blue-50',
      title: firstItem?.title ?? '종목별 채용 공고 전체보기',
      desc: firstItem?.desc ?? <><strong className="font-semibold text-gray-900">전체보기</strong>에서 <strong className="font-semibold text-gray-900">지역별 필터와 검색 기능</strong>을 이용할 수 있습니다.</>,
    },
    {
      icon: ArrowUp, color: 'text-orange-600', bg: 'bg-orange-50',
      title: <>기존 글 끌어올리기 <span className="text-[11px] font-semibold text-gray-500">(업데이트 예정)</span></>,
      desc: <>등록한 채용 공고는 <strong className="font-semibold text-gray-900">5일에 한 번</strong> 목록 상단으로 끌어올릴 수 있습니다.</>,
    },
    {
      icon: UserCheck, color: 'text-emerald-600', bg: 'bg-emerald-50',
      title: '구직 희망 및 이력서 공개 설정',
      desc: <><strong className="font-semibold text-gray-900">'나의 이력서'에서 구직 ON / OFF</strong>를 설정할 수 있으며, <strong className="font-semibold text-gray-900">사진을 포함한 항목별 공개·비공개</strong> 설정이 가능합니다.</>,
    },
    {
      icon: MessageCircle, color: 'text-violet-600', bg: 'bg-violet-50',
      title: <>채용 제안 메시지 <span className="text-[11px] font-semibold text-red-500">(유료)</span></>,
      desc: <>채용 기관은 구직 희망자에게 <strong className="font-semibold text-gray-900">채용 제안 메시지를 보내고 채팅</strong>할 수 있습니다.</>,
    },
  ];

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden flex flex-col h-full">
      <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/70">
        <h2 className="text-base font-bold text-gray-900">{title}</h2>
      </div>
      <div className={`flex-1 grid divide-gray-100 ${columns === 4 ? 'md:grid-cols-2 xl:grid-cols-4 divide-y md:divide-y-0 md:divide-x' : 'grid-cols-1 divide-y'}`}>
        {items.map((item, i) => {
          const Icon = item.icon;
          return (
            <div key={i} className="px-5 py-4 flex gap-3">
              <span className={`w-8 h-8 rounded-lg ${item.bg} ${item.color} flex items-center justify-center shrink-0`}>
                <Icon className="w-4 h-4" />
              </span>
              <div>
                <h3 className="text-sm font-bold text-gray-900">{item.title}</h3>
                <p className="mt-1 text-xs leading-5 text-gray-500">{item.desc}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// 주변 구직 희망자 패널 — 기업 회원/관리자가 아니면 블러 + 잠금 안내로 대체(구인 게시판 메인/포지션별 서브페이지 공용)
function SeekerPanel({
  seekers, loading, locked, onSelectSeeker, emptyMessage, intro,
}: {
  seekers: any[]; loading: boolean; locked: boolean; onSelectSeeker: (seeker: any) => void; emptyMessage?: string; intro?: boolean;
}) {
  const content = (
    <>
      <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-3">
        <Users className="w-5 h-5 text-primary" />
        주변 구직 희망자
        {seekers.length > 0 && <span className="text-sm font-normal text-gray-400">{seekers.length}명</span>}
      </h3>
      {intro && (
        <div className="rounded-xl bg-primary/5 border border-primary/10 px-4 py-3 mb-4 flex gap-3">
          <span className="w-8 h-8 rounded-lg bg-white text-primary flex items-center justify-center shrink-0">
            <Send className="w-4 h-4" />
          </span>
          <p className="text-xs leading-5 text-gray-600">구직 희망자를 클릭하면 이력서 확인 및 채용 제안 메시지를 보낼 수 있습니다.</p>
        </div>
      )}
      {loading ? (
        <div className="text-center py-12 text-gray-500">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          로딩 중...
        </div>
      ) : seekers.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 text-center py-12 text-gray-500">
          <Users className="w-10 h-10 mx-auto mb-2 text-gray-300" />
          <p>{emptyMessage || '구직 중인 사용자가 없습니다.'}</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
          {seekers.map((seeker: any) => (
            <div
              key={seeker.id}
              className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => onSelectSeeker(seeker)}
            >
              <div className="flex items-start gap-3">
                {seeker.photo_url ? (
                  <img src={seeker.photo_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="w-5 h-5 text-primary" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-gray-900 text-sm">{seeker.user_name || '이름 비공개'}</h4>
                  {seeker.desired_position && (
                    <p className="text-xs text-primary font-medium">{seeker.desired_position}</p>
                  )}
                </div>
                <div className="flex flex-wrap gap-1 shrink-0">
                  {seeker.desired_work_type && seeker.desired_work_type !== 'ANY' && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                      {{ FULL_TIME: '정규직', CONTRACT: '계약직', PART_TIME: '파트타임', FREELANCE: '프리랜서' }[seeker.desired_work_type as string] || seeker.desired_work_type}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between mt-1.5 ml-[52px]">
                {seeker.desired_location && (
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <MapPin className="w-3 h-3" />{seeker.desired_location}
                  </p>
                )}
                <span className="text-xs text-gray-400">
                  {new Date(seeker.updated_at || seeker.created_at).toLocaleDateString('ko-KR')}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );

  if (!locked) return <div>{content}</div>;

  return (
    <div className="relative overflow-hidden">
      <div className="blur-[4px] opacity-40 pointer-events-none select-none" aria-hidden="true">
        {content}
      </div>
      <div className="absolute inset-0 z-10 flex items-center justify-center px-6 bg-white/20 backdrop-blur-[1px]">
        <div className="rounded-xl bg-white/95 border border-gray-200 shadow-sm px-6 py-4 text-center">
          <div className="flex items-center justify-center gap-2 text-gray-800 font-bold text-sm sm:text-base">
            <Lock className="w-5 h-5 text-primary" />
            <span>주변 구직 희망자는 기업 회원만 열람 가능합니다.</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// 포지션별 채용 서브페이지 (트레이너/필라테스 강사/기타 종목/요가 강사/아카데미 교육 강사 — 대강 제외)
function PositionJobsView({
  subviewKey, jobs, seekers, loading, loadingSeekers, canViewSeekers,
  onSelectJob, onSelectSeeker, onBack, user, navigate,
  provinceFilter, districtFilter, onProvinceChange, onDistrictChange,
}: {
  subviewKey: PositionSubviewKey;
  jobs: any[];
  seekers: any[];
  loading: boolean;
  loadingSeekers: boolean;
  canViewSeekers: boolean;
  onSelectJob: (job: any) => void;
  onSelectSeeker: (seeker: any) => void;
  onBack: () => void;
  user: any;
  navigate: (path: string) => void;
  provinceFilter: string;
  districtFilter: string;
  onProvinceChange: (v: string) => void;
  onDistrictChange: (v: string) => void;
}) {
  const cfg = POSITION_SUBVIEWS[subviewKey];
  const positionJobs = useMemo(
    () => jobs.filter((j: any) => j.position === cfg.value),
    [jobs, cfg.value],
  );
  const positionSeekers = useMemo(
    () => seekers.filter((s: any) => s.desired_position === cfg.value),
    [seekers, cfg.value],
  );
  const boardId = `${subviewKey}-board`;
  const facilitiesId = `${subviewKey}-facilities`;
  const seekersId = `${subviewKey}-seekers`;
  const scrollTo = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const canPost = user?.role === 'employer' || user?.role === 'admin';
  const boardFooter = (
    <div className={`grid ${canPost ? 'grid-cols-2' : 'grid-cols-1'} gap-2`}>
      {canPost && (
        <button
          type="button"
          onClick={() => navigate(`/employer/recruitment?tab=job-postings&new=1&position=${encodeURIComponent(cfg.value)}`)}
          className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary-600 transition-colors"
        >
          <Plus className="w-4 h-4" />
          새 구인글 등록하기
        </button>
      )}
      <button
        type="button"
        className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-accent/20 bg-accent/10 text-accent-700 text-sm font-semibold cursor-default"
      >
        <ArrowUp className="w-4 h-4" />
        기존 글 끌어올리기 <span className="text-[11px] font-semibold opacity-75">(업데이트 예정)</span>
      </button>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* 뒤로가기 */}
      <button
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-500 hover:text-gray-900 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        구인구직으로
      </button>

      {/* 히어로 — 포지션 소개 + 바로가기 3종 */}
      <section
        className="rounded-[28px] border overflow-hidden relative"
        style={{ borderColor: cfg.border, background: `linear-gradient(135deg, #fff 0%, ${cfg.soft} 100%)` }}
      >
        <div className="relative px-6 sm:px-8 lg:px-10 py-8 lg:py-10 grid lg:grid-cols-[1fr_auto] gap-8 items-center">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span
                className="w-14 h-14 rounded-2xl bg-white border shadow-sm flex items-center justify-center shrink-0"
                style={{ borderColor: cfg.border, color: cfg.color }}
              >
                <PositionIcon type={cfg.icon} className="w-8 h-8" />
              </span>
              <div>
                <div className="text-[11px] sm:text-xs font-extrabold uppercase tracking-[.13em]" style={{ color: cfg.color }}>
                  STAMP FIT CAREERS
                </div>
                <h1 className="text-2xl sm:text-3xl lg:text-[34px] leading-tight font-extrabold text-gray-950 mt-1">
                  {cfg.label} 채용{cfg.subtext && <span className="text-base font-semibold text-gray-400 ml-2">{cfg.subtext}</span>}
                </h1>
              </div>
            </div>
            <p className="max-w-2xl text-sm sm:text-base leading-7 text-gray-600">{cfg.description}</p>
          </div>
          <div className="grid grid-cols-3 gap-2 sm:gap-3 min-w-0 lg:min-w-[390px]">
            {[
              { id: boardId, icon: ClipboardList, label: '채용 게시판' },
              { id: facilitiesId, icon: MapPinned, label: '주변 구인시설' },
              { id: seekersId, icon: UsersRound, label: '주변 구직자' },
            ].map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => scrollTo(item.id)}
                className="group bg-white/90 border rounded-2xl px-3 sm:px-4 py-4 text-center hover:-translate-y-0.5 hover:shadow-md transition-all"
                style={{ borderColor: cfg.border }}
              >
                <span className="mx-auto w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: cfg.soft, color: cfg.color }}>
                  <item.icon className="w-4.5 h-4.5" />
                </span>
                <span className="block text-xs sm:text-sm font-bold text-gray-800 mt-2">{item.label}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* 지역 필터 */}
      <section className="bg-white rounded-2xl border border-gray-200 px-5 py-4 shadow-sm flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-sm font-extrabold text-gray-900">지역으로 좁혀보기</h2>
          <p className="text-xs text-gray-500 mt-1">채용공고 · 지도 · 구직 희망자에 동일하게 적용됩니다.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <MapPin className="w-4 h-4 shrink-0" style={{ color: cfg.color }} />
          <select
            value={provinceFilter}
            onChange={(e) => onProvinceChange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white focus:ring-2 focus:ring-primary focus:border-primary"
          >
            <option value="">전체 지역</option>
            {getAllProvinces().map((province) => (
              <option key={province} value={province}>{province}</option>
            ))}
          </select>
          <select
            value={districtFilter}
            onChange={(e) => onDistrictChange(e.target.value)}
            disabled={!provinceFilter}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            <option value="">전체 시/군/구</option>
            {provinceFilter && getDistricts(provinceFilter).map((district) => (
              <option key={district} value={district}>{district}</option>
            ))}
          </select>
        </div>
      </section>

      {/* 메인 콘텐츠: 좌(지도+구직 희망자) / 우(게시판, 좌측과 바닥 높이 맞춰 스크롤 시 sticky) */}
      <section className="grid grid-cols-1 xl:grid-cols-[minmax(0,.78fr)_minmax(0,1.42fr)] gap-6">
        <div className="min-w-0 space-y-6">
          <div id={facilitiesId}>
            <div className="flex items-end justify-between gap-3 mb-3 px-1">
              <h2 className="text-xl font-extrabold text-gray-950">내 주변 구인 시설</h2>
            </div>
            <JobMap jobs={positionJobs} centerAddress={user?.address || '서울특별시'} onSelectJob={onSelectJob} />
          </div>

          <div id={seekersId}>
            <div className="flex items-end justify-between gap-3 mb-3 px-1">
              <h2 className="text-xl font-extrabold text-gray-950 flex items-center gap-2">
                주변 구직 희망자
                {positionSeekers.length > 0 && <span className="text-sm font-semibold" style={{ color: cfg.color }}>{positionSeekers.length}명</span>}
              </h2>
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 min-h-[500px]">
              <SeekerPanel
                seekers={positionSeekers}
                loading={loadingSeekers}
                locked={!canViewSeekers}
                onSelectSeeker={onSelectSeeker}
                emptyMessage={`${cfg.label} 희망 구직자가 없습니다.`}
                intro
              />
            </div>
          </div>
        </div>

        {/* 그리드 stretch로 좌측(지도+구직 희망자) 전체 높이와 동일하게 맞춘 뒤, 그 안에서만 sticky */}
        <div id={boardId} className="min-w-0">
          <div className="xl:sticky xl:top-24 h-full flex flex-col">
            <div className="flex items-center justify-between gap-4 mb-3 px-1">
              <div className="flex items-center gap-3 min-w-0">
                <span
                  className="w-11 h-11 rounded-xl border flex items-center justify-center shrink-0"
                  style={{ borderColor: cfg.border, backgroundColor: cfg.soft, color: cfg.color }}
                >
                  <PositionIcon type={cfg.icon} className="w-6 h-6" />
                </span>
                <h2 className="text-xl font-extrabold text-gray-950 leading-tight">{cfg.label} 채용</h2>
              </div>
              <span className="hidden sm:flex items-center gap-1.5 text-xs text-gray-500 shrink-0">
                <MousePointerClick className="w-4 h-4" />
                공고를 클릭하면 상세정보를 확인할 수 있습니다.
              </span>
            </div>
            <div className="flex-1 min-h-0">
              <JobPositionBoard
                title={`${cfg.label} 채용`}
                positionValue={cfg.value}
                jobs={jobs}
                loading={loading}
                onSelectJob={onSelectJob}
                pageSize={20}
                emptyMessage={`등록된 ${cfg.label} 채용이 없습니다.`}
                footer={boardFooter}
              />
            </div>
          </div>
        </div>
      </section>

      <JobServiceGuide columns={4} />
    </div>
  );
}

// 대강(대타 강사) 탭 — "Stamp fit - Substitute instructor subpage (sky) v4.html" 그대로 이식.
// 포지션별 서브페이지와 달리 구직 희망자 패널이 없고(구인 공고만), 지도가 게시판보다 짧을 수 있어
// 좌측을 stretch하지 않고 게시판에 바로 sticky를 건다(목업과 동일 — items-start + self-start)
const SUBSTITUTE_THEME = { color: '#0284C7', border: '#BAE6FD', soft: '#F0F9FF' };

function SubstituteJobsView({
  jobs, loading, user, navigate, onSelectJob,
  provinceFilter, districtFilter, onProvinceChange, onDistrictChange,
}: {
  jobs: any[];
  loading: boolean;
  user: any;
  navigate: (path: string) => void;
  onSelectJob: (job: any) => void;
  provinceFilter: string;
  districtFilter: string;
  onProvinceChange: (v: string) => void;
  onDistrictChange: (v: string) => void;
}) {
  const substituteJobs = useMemo(() => jobs.filter((j: any) => j.position === '대강'), [jobs]);
  const boardId = 'substitute-board';
  const facilitiesId = 'substitute-facilities';
  const scrollTo = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const canPost = user?.role === 'employer' || user?.role === 'admin';
  const boardFooter = (
    <div className={`grid ${canPost ? 'grid-cols-2' : 'grid-cols-1'} gap-2`}>
      {canPost && (
        <button
          type="button"
          onClick={() => navigate(`/employer/recruitment?tab=job-postings&new=1&position=${encodeURIComponent('대강')}`)}
          className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary-600 transition-colors"
        >
          <Plus className="w-4 h-4" />
          대타 강사 구인 글 등록하기
        </button>
      )}
      <button
        type="button"
        className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-accent/20 bg-accent/10 text-accent-700 text-sm font-semibold cursor-default"
      >
        <ArrowUp className="w-4 h-4" />
        기존 글 끌어올리기 <span className="text-[11px] font-semibold opacity-75">(업데이트 예정)</span>
      </button>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* 히어로 — 대타 강사 소개 + 바로가기 2종(구직 희망자 카드 없음) */}
      <section
        className="rounded-[28px] border overflow-hidden relative"
        style={{ borderColor: SUBSTITUTE_THEME.border, background: `linear-gradient(135deg, #fff 0%, ${SUBSTITUTE_THEME.soft} 100%)` }}
      >
        <div className="relative px-6 sm:px-8 lg:px-10 py-8 lg:py-10 grid lg:grid-cols-[1fr_auto] gap-8 items-center">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span
                className="w-14 h-14 rounded-2xl bg-white border shadow-sm flex items-center justify-center shrink-0"
                style={{ borderColor: SUBSTITUTE_THEME.border, color: SUBSTITUTE_THEME.color }}
              >
                <Repeat2 className="w-8 h-8" aria-label="대타 강사" />
              </span>
              <div>
                <div className="text-[11px] sm:text-xs font-extrabold uppercase tracking-[.13em]" style={{ color: SUBSTITUTE_THEME.color }}>
                  STAMP FIT SUBSTITUTE
                </div>
                <h1 className="text-2xl sm:text-3xl lg:text-[34px] leading-tight font-extrabold text-gray-950 mt-1">대타 강사</h1>
              </div>
            </div>
            <p className="max-w-2xl text-sm sm:text-base leading-7 text-gray-600">갑작스럽게 필요한 대타 강사 공고를 빠르게 확인하고, 내 주변의 대타 수업을 한 페이지에서 찾아보세요.</p>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:gap-3 min-w-0 lg:min-w-[300px]">
            {[
              { id: boardId, icon: CalendarClock, label: '대타 공고' },
              { id: facilitiesId, icon: MapPinned, label: '주변 대타 수업' },
            ].map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => scrollTo(item.id)}
                className="group bg-white/90 border rounded-2xl px-4 py-4 text-center hover:-translate-y-0.5 hover:shadow-md transition-all"
                style={{ borderColor: SUBSTITUTE_THEME.border }}
              >
                <span className="mx-auto w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: SUBSTITUTE_THEME.soft, color: SUBSTITUTE_THEME.color }}>
                  <item.icon className="w-4.5 h-4.5" />
                </span>
                <span className="block text-xs sm:text-sm font-bold text-gray-800 mt-2">{item.label}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* 지역 필터 */}
      <section className="bg-white rounded-2xl border border-gray-200 px-5 py-4 shadow-sm flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-sm font-extrabold text-gray-900">지역으로 좁혀보기</h2>
          <p className="text-xs text-gray-500 mt-1">대타 공고와 지도에 동일하게 적용됩니다.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <MapPin className="w-4 h-4 shrink-0" style={{ color: SUBSTITUTE_THEME.color }} />
          <select
            value={provinceFilter}
            onChange={(e) => onProvinceChange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white focus:ring-2 focus:ring-primary focus:border-primary"
          >
            <option value="">전체 지역</option>
            {getAllProvinces().map((province) => (
              <option key={province} value={province}>{province}</option>
            ))}
          </select>
          <select
            value={districtFilter}
            onChange={(e) => onDistrictChange(e.target.value)}
            disabled={!provinceFilter}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            <option value="">전체 시/군/구</option>
            {provinceFilter && getDistricts(provinceFilter).map((district) => (
              <option key={district} value={district}>{district}</option>
            ))}
          </select>
        </div>
      </section>

      {/* 메인 콘텐츠: 좌(지도) / 우(게시판) — 구직 희망자 패널은 없지만, 지도보다 게시판이 짧을 때
          sticky가 붙을 여유 공간이 없어 스크롤 중 통째로 사라지는 문제가 있어 grid stretch로 높이를 맞춘다 */}
      <section className="grid grid-cols-1 xl:grid-cols-[minmax(0,.9fr)_minmax(0,1.3fr)] gap-6">
        <div id={facilitiesId} className="min-w-0">
          <div className="flex items-end justify-between gap-3 mb-3 px-1">
            <h2 className="text-xl font-extrabold text-gray-950">내 주변 대타 수업</h2>
            <span className="text-xs text-gray-500 hidden sm:inline">지도 마커를 선택해 공고 확인</span>
          </div>
          <JobMap jobs={substituteJobs} centerAddress={user?.address || '서울특별시'} onSelectJob={onSelectJob} />
        </div>

        <div id={boardId} className="min-w-0">
          <div className="xl:sticky xl:top-24 h-full flex flex-col">
            <div className="flex items-center justify-between gap-4 mb-3 px-1">
              <div className="flex items-center gap-3 min-w-0">
                <span
                  className="w-11 h-11 rounded-xl border flex items-center justify-center shrink-0"
                  style={{ borderColor: SUBSTITUTE_THEME.border, backgroundColor: SUBSTITUTE_THEME.soft, color: SUBSTITUTE_THEME.color }}
                >
                  <Repeat2 className="w-6 h-6" />
                </span>
                <div className="min-w-0">
                  <h2 className="text-xl font-extrabold text-gray-950 leading-tight">대타 강사 공고</h2>
                  <p className="text-xs text-gray-500 mt-1">날짜와 지역을 확인하고 필요한 수업에 바로 지원하세요.</p>
                </div>
              </div>
              <span className="hidden sm:flex items-center gap-1.5 text-xs text-gray-500 shrink-0">
                <MousePointerClick className="w-4 h-4" />
                공고 클릭 시 상세정보 확인
              </span>
            </div>
            <div className="flex-1 min-h-0">
              <JobPositionBoard
                title="대타 강사 공고"
                positionValue="대강"
                jobs={jobs}
                loading={loading}
                onSelectJob={onSelectJob}
                pageSize={20}
                emptyMessage="등록된 대타 강사 공고가 없습니다."
                footer={boardFooter}
              />
            </div>
          </div>
        </div>
      </section>

      <JobServiceGuide
        title="대타 강사 서비스 이용 안내"
        columns={4}
        firstItem={{
          title: '대타 강사 공고 전체보기',
          desc: <><strong className="font-semibold text-gray-900">전체보기</strong>에서 <strong className="font-semibold text-gray-900">지역별 필터와 검색 기능</strong>으로 필요한 대타 수업을 빠르게 찾을 수 있습니다.</>,
        }}
      />
    </div>
  );
}

export default function HomePage() {
  const [classTab, setClassTab] = useState<ClassTabKey>(() => {
    const t = new URLSearchParams(window.location.search).get('tab');
    const valid: ClassTabKey[] = ['main', 'certificate', 'online', 'completion', 'workshop', 'badge', 'wallet'];
    return (valid as string[]).includes(t || '') ? (t as ClassTabKey) : 'main';
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('전체');
  const [classTypeFilter, setClassTypeFilter] = useState<'ALL' | 'CERTIFICATE' | 'COMPLETION' | 'WORKSHOP' | 'OTHER'>('ALL');
  const [showLoginSuccess, setShowLoginSuccess] = useState(false);
  const [premiumProviders, setPremiumProviders] = useState<PremiumProvider[]>([]);
  const [loadingPremium, setLoadingPremium] = useState(true);
  const [premiumIntro, setPremiumIntro] = useState('');
  const [classes, setClasses] = useState<ClassItem[]>([]);
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [categoryTree, setCategoryTree] = useState<CategoryNode[]>([]);
  const [viewMode, setViewMode] = useState<'list' | 'card'>('card'); // 기본값: 카드형
  const [allItemsStatusFilter, setAllItemsStatusFilter] = useState<'active' | 'completed'>('active');
  const [classProvinceFilter, setClassProvinceFilter] = useState('');
  const [classDistrictFilter, setClassDistrictFilter] = useState('');
  const [onlyMyInstitutionAllItems, setOnlyMyInstitutionAllItems] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const loginSuccess = useAuthStore((state) => state.loginSuccess);

  // 상단 메인 섹션: 수강신청(classes) / 구인구직(jobs) / 대강(substitute) / 포지션별 서브페이지 — 탭 바·게시판 아이콘에서 전환
  type HomeView = 'classes' | 'jobs' | 'substitute' | 'challenge' | PositionSubviewKey;
  const isJobRelatedView = (v: string | null): v is 'jobs' | 'substitute' | PositionSubviewKey =>
    v === 'jobs' || v === 'substitute' || POSITION_SUBVIEW_KEYS.includes(v as PositionSubviewKey);
  const [view, setView] = useState<HomeView>(() => {
    const v = searchParams.get('view');
    if (isJobRelatedView(v) || v === 'challenge') return v as HomeView;
    return 'classes';
  });

  // URL(view/tab 쿼리)이 바뀔 때마다 상태를 다시 동기화 — 헤더 로고(Link to="/")처럼
  // 쿼리 없는 "/"로 이동해도 탭 상태가 초기값(교육 메인)으로 리셋되도록 한다.
  useEffect(() => {
    const v = searchParams.get('view');
    setView(isJobRelatedView(v) || v === 'challenge' ? (v as HomeView) : 'classes');

    const t = searchParams.get('tab');
    const validTabs: ClassTabKey[] = ['main', 'certificate', 'online', 'completion', 'workshop', 'badge', 'wallet'];
    setClassTab((validTabs as string[]).includes(t || '') ? (t as ClassTabKey) : 'main');
  }, [searchParams]);

  // 상단 탭(뷰/카테고리) 전환 또는 진입 시 항상 최상단으로 스크롤 (현재 스크롤 위치 유지 방지)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [view, classTab]);

  // 회원가입 축하 모달
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  // 강사 모달 상태
  const [selectedInstructor, setSelectedInstructor] = useState<any>(null);
  const [showInstructorModal, setShowInstructorModal] = useState(false);

  // 구인구직 탭 데이터
  const [jobPostings, setJobPostings] = useState<any[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [jobSeekers, setJobSeekers] = useState<any[]>([]);
  const [loadingJobSeekers, setLoadingJobSeekers] = useState(false);
  const [jobSubTab, setJobSubTab] = useState<'postings' | 'seekers'>('postings');
  const [jobProvinceFilter, setJobProvinceFilter] = useState('');
  const [jobDistrictFilter, setJobDistrictFilter] = useState('');
  const [selectedJob, setSelectedJob] = useState<any>(null);
  const [selectedSeeker, setSelectedSeeker] = useState<any>(null);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [loadingSeekerDetail, setLoadingSeekerDetail] = useState(false);
  const [seekerDetail, setSeekerDetail] = useState<any>(null);
  const [appliedJobIds, setAppliedJobIds] = useState<Set<string>>(new Set());
  const [applyingJob, setApplyingJob] = useState(false);
  const user = useAuthStore((state) => state.user);
  // 주변 구직 희망자 열람 권한 — 기업 회원/관리자만 (구인 게시판 메인 + 포지션별 서브페이지 공용)
  const canViewSeekers = user?.role === 'employer' || user?.role === 'admin';

  // 우수 인증 교육 기관 목록 조회
  useEffect(() => {
    const fetchPremiumProviders = async () => {
      try {
        const response = await fetch('/api/v1/providers/premium');
        if (response.ok) {
          const data = await response.json();
          setPremiumProviders(data.providers || []);
        }
      } catch (error) {
        console.error('Failed to fetch premium providers:', error);
      } finally {
        setLoadingPremium(false);
      }
    };

    fetchPremiumProviders();
  }, []);

  // 우수 인증 교육 기관 소개 문구 조회 (관리자 편집값)
  useEffect(() => {
    const fetchPremiumIntro = async () => {
      try {
        const response = await fetch('/api/v1/site-settings/premium_providers_intro');
        if (response.ok) {
          const data = await response.json();
          setPremiumIntro(data.data?.value || '');
        }
      } catch (error) {
        console.error('Failed to fetch premium intro:', error);
      }
    };

    fetchPremiumIntro();
  }, []);

  // 구인구직 데이터 로딩 (탭 전환 시) — 구인구직/대강/포지션별 서브페이지 뷰 모두 동일 데이터 사용
  useEffect(() => {
    if (!isJobRelatedView(view)) return;

    const fetchJobData = async () => {
      setLoadingJobs(true);
      setLoadingJobSeekers(true);
      try {
        const [jobsRes, seekersRes] = await Promise.all([
          fetch('/api/v1/jobs?limit=50'),
          fetch('/api/v1/job-seekers?limit=50', { credentials: 'include' }),
        ]);
        if (jobsRes.ok) {
          const jobsData = await jobsRes.json();
          setJobPostings(jobsData.data || []);
        }
        if (seekersRes.ok) {
          const seekersData = await seekersRes.json();
          setJobSeekers(seekersData.data || []);
        }
      } catch (err) {
        console.error('Failed to fetch job data:', err);
      } finally {
        setLoadingJobs(false);
        setLoadingJobSeekers(false);
      }
    };

    fetchJobData();
  }, [view]);

  // 지역(시/도, 시/군/구) 필터 — 구인구직/대강 탭의 모든 섹션(지도·포지션별 게시판·구직 희망자)이 공유
  const filteredJobPostings = useMemo(() => {
    if (!jobProvinceFilter) return jobPostings;
    return jobPostings.filter((job: any) =>
      job.work_location_province === jobProvinceFilter
      && (!jobDistrictFilter || job.work_location_district === jobDistrictFilter)
    );
  }, [jobPostings, jobProvinceFilter, jobDistrictFilter]);

  const filteredJobSeekers = useMemo(() => {
    if (!jobProvinceFilter) return jobSeekers;
    return jobSeekers.filter((seeker: any) =>
      seeker.desired_location_province === jobProvinceFilter
      && (!jobDistrictFilter || seeker.desired_location_district === jobDistrictFilter)
    );
  }, [jobSeekers, jobProvinceFilter, jobDistrictFilter]);

  // 일반 회원: 본인이 이미 지원한 공고 ID 목록 로딩 (지원하기 버튼 상태 표시용)
  useEffect(() => {
    if (!user || user.role !== 'user') {
      setAppliedJobIds(new Set());
      return;
    }
    fetch('/api/v1/applications', { credentials: 'include' })
      .then(res => (res.ok ? res.json() : { applications: [] }))
      .then(data => {
        const ids = new Set<string>((data.applications || []).map((a: any) => a.job_posting_id));
        setAppliedJobIds(ids);
      })
      .catch(err => console.error('Failed to fetch applied job ids:', err));
  }, [user]);

  const handleApplyJob = async (job: any) => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.role && user.role !== 'user') {
      alert('일반 회원만 채용에 지원할 수 있습니다.');
      return;
    }
    if (!confirm(`[${job.title}] 공고에 지원하시겠습니까?\n현재 이력서가 그대로 제출됩니다.`)) return;

    setApplyingJob(true);
    try {
      const response = await fetch(`/api/v1/jobs/${job.id}/apply`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await response.json();
      if (!response.ok) {
        if (data.code === 'RESUME_REQUIRED' || data.code === 'RESUME_EMPTY') {
          if (confirm(`${data.error}\n\n지금 이력서 작성 페이지로 이동할까요?`)) {
            navigate('/profile/resume');
          }
          return;
        }
        throw new Error(data.error || '지원에 실패했습니다.');
      }
      alert('지원이 완료되었습니다.');
      setAppliedJobIds(prev => new Set(prev).add(job.id));
      setSelectedJob(null);
    } catch (err: any) {
      alert(err.message || '지원에 실패했습니다.');
    } finally {
      setApplyingJob(false);
    }
  };

  const handleViewSeeker = async (seeker: any) => {
    setSelectedSeeker(seeker);
    setLoadingSeekerDetail(true);
    try {
      const res = await fetch(`/api/v1/job-seekers/${seeker.user_id}`, { credentials: 'include' });
      if (res.ok) {
        const result = await res.json();
        setSeekerDetail(result.data);
      }
    } catch (err) {
      console.error('Failed to fetch seeker detail:', err);
    } finally {
      setLoadingSeekerDetail(false);
    }
  };

  // 전체 수업 목록 조회
  useEffect(() => {
    const fetchClasses = async () => {
      try {
        setLoadingClasses(true);
        const response = await fetch('/api/v1/classes', {
          credentials: 'include',
        });

        if (response.ok) {
          const data = await response.json();
          setClasses(data.classes || []);
        }
      } catch (error) {
        console.error('Failed to fetch classes:', error);
        setClasses([]);
      } finally {
        setLoadingClasses(false);
      }
    };

    fetchClasses();

    // 모집중/진행중/마감 등 상태는 서버에서 날짜 기준으로 매번 다시 계산되므로,
    // 홈 탭을 오래 열어둔 채 방치하면 최초 로드 시점 상태로 굳어버림 → 포커스 복귀 시 재조회
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchClasses();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleVisibility);
    };
  }, []);

  // 전체 카테고리 트리 조회 (좌측 필터에 1·2차 전부 노출 — 수업 등록 여부 무관)
  useEffect(() => {
    const fetchCategoryTree = async () => {
      try {
        const res = await fetch('/api/v1/categories', { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setCategoryTree((data.data as CategoryNode[]) || []);
        }
      } catch (error) {
        console.error('Failed to fetch category tree:', error);
        setCategoryTree([]);
      }
    };
    fetchCategoryTree();
  }, []);

  useEffect(() => {
    const loginStatus = searchParams.get('login');
    const userId = searchParams.get('user_id');
    const provider = searchParams.get('provider');
    const isNew = searchParams.get('is_new') === 'true';
    const name = searchParams.get('name') || undefined;
    const email = searchParams.get('email') || undefined;

    if (loginStatus === 'success' && userId && provider) {
      console.log('Login success:', { userId, provider, isNew, name, email });

      // authStore 업데이트
      loginSuccess(userId, provider, isNew, name, email);
      setShowLoginSuccess(true);

      // URL 파라미터 정리
      searchParams.delete('login');
      searchParams.delete('user_id');
      searchParams.delete('provider');
      searchParams.delete('is_new');
      searchParams.delete('name');
      searchParams.delete('email');
      setSearchParams(searchParams, { replace: true });

      // 3초 후 알림 숨기기
      setTimeout(() => setShowLoginSuccess(false), 3000);
    }
  }, [searchParams, setSearchParams, loginSuccess]);

  // 이메일 인증 완료 후 홈페이지 도착 감지 (축하 모달)
  useEffect(() => {
    const url = new URL(window.location.href);
    const hash = url.hash;
    const welcomeParam = url.searchParams.get('welcome');

    // Supabase 이메일 인증 후 리다이렉트 (hash에 type=signup 포함)
    if (hash) {
      const hashParams = new URLSearchParams(hash.substring(1));
      const type = hashParams.get('type');
      if (type === 'signup' || type === 'email') {
        setShowWelcomeModal(true);
        window.history.replaceState(null, '', '/');
        return;
      }
    }

    // ?welcome=true 파라미터로도 감지
    if (welcomeParam === 'true') {
      setShowWelcomeModal(true);
      window.history.replaceState(null, '', '/');
    }
  }, []);

  // 우수 인증 교육 기관 랜덤 순서 (업종 구분 임시 비활성화 — 전체 표시)
  const filteredPremiumProviders = useMemo(() => {
    const shuffled = [...premiumProviders];
    // Fisher-Yates 셔플
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }, [premiumProviders]);

  // 필터링된 수업 목록 (전체 항목 패널용 — 상태 탭 포함, 업종 구분 임시 비활성화)
  const filteredClasses = classes.filter((cls) => {
    const q = searchQuery.toLowerCase();
    const matchesSearch =
      cls.title.toLowerCase().includes(q) || cls.provider_name.toLowerCase().includes(q);
    const matchesType = classTypeFilter === 'ALL' || cls.class_type === classTypeFilter;
    const matchesStatus =
      allItemsStatusFilter === 'completed'
        ? cls.status !== 'RECRUITING'
        : cls.status === 'RECRUITING';
    const matchesMyInstitution =
      !onlyMyInstitutionAllItems || (!!user && cls.provider_id === user.id);
    const matchesProvince = !classProvinceFilter || cls.location_province === classProvinceFilter;
    const matchesDistrict = !classDistrictFilter || cls.location_district === classDistrictFilter;
    return matchesSearch && matchesType && matchesStatus && matchesMyInstitution && matchesProvince && matchesDistrict;
  });

  // 인기 클래스용 수업 목록 (업종 구분 임시 비활성화, 모집중인 것만)
  const businessFilteredClasses = useMemo(
    () => classes.filter((c) => c.status === 'RECRUITING'),
    [classes]
  );

  // 우수 인증 교육 기관 ID 집합 (카드 강사명 노출용)
  const premiumProviderIds = useMemo(
    () => new Set(premiumProviders.map((p) => p.id)),
    [premiumProviders]
  );

  // 카테고리 탭별 전체 수업 목록 (업종 필터 미적용 — 해당 유형 전체)
  const categoryClasses = useMemo(() => {
    // API가 bumped_at desc 정렬 제공 → 그대로 사용
    return {
      certificate: classes.filter((c) => c.class_type === 'CERTIFICATE'),
      online: classes.filter((c) => c.location_type === 'ONLINE'),
      completion: classes.filter((c) => c.class_type === 'COMPLETION'),
      workshop: classes.filter((c) => c.class_type === 'WORKSHOP'),
    };
  }, [classes]);

  // 상태 라벨
  const getStatusLabel = (status: string) => {
    const labels: Record<string, { text: string; color: string }> = {
      SCHEDULED: { text: '모집예정', color: 'bg-blue-100 text-blue-700' },
      RECRUITING: { text: '모집중', color: 'bg-green-100 text-green-700' },
      CLOSED: { text: '모집마감', color: 'bg-red-100 text-red-700' },
      IN_PROGRESS: { text: '진행중', color: 'bg-blue-100 text-blue-700' },
      COMPLETED: { text: '종료', color: 'bg-gray-100 text-gray-700' },
    };
    return labels[status] || labels.RECRUITING;
  };

  // 장소 텍스트
  const getLocationText = (cls: ClassItem) => {
    if (cls.location_type === 'ONLINE') {
      return '온라인';
    } else if (cls.location_type === 'OFFLINE') {
      return cls.location_address || '-';
    } else {
      return `온/오프라인`;
    }
  };

  // 수업 카드의 강사 이름 클릭 핸들러
  const handleInstructorClick = async (instructorId: string) => {
    try {
      const response = await fetch(`/api/v1/instructors/${instructorId}`, {
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Failed to fetch instructor');

      const data = await response.json();
      setSelectedInstructor(data.instructor);
      setShowInstructorModal(true);
    } catch (error) {
      console.error('Failed to fetch instructor:', error);
    }
  };

  return (
    <div className="px-6 sm:px-10 lg:px-16 pb-2">
      {/* 회원가입 축하 모달 */}
      {showWelcomeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* 배경 오버레이 */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowWelcomeModal(false)}
          />

          {/* 모달 */}
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center animate-bounceIn">
            {/* 닫기 버튼 */}
            <button
              onClick={() => setShowWelcomeModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            {/* 축하 아이콘 */}
            <div className="w-24 h-24 bg-gradient-to-br from-accent/20 to-primary/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <PartyPopper className="w-12 h-12 text-accent" />
            </div>

            {/* 타이틀 */}
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              회원가입을 축하합니다!
            </h2>

            {/* 설명 */}
            <p className="text-gray-600 mb-2">
              가입이 완료되었습니다.
            </p>
            <p className="text-gray-500 text-sm mb-8">
              스탬핏에서 다양한 피트니스 교육과 자격 인증을<br />
              만나보세요!
            </p>

            {/* 버튼 */}
            <button
              onClick={() => setShowWelcomeModal(false)}
              className="w-full px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors"
            >
              시작하기
            </button>
          </div>
        </div>
      )}

      {/* Login Success Toast */}
      {showLoginSuccess && (
        <div className="fixed top-4 right-4 z-50 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2 animate-slideUp">
          <CheckCircle className="w-5 h-5" />
          <span>로그인에 성공했습니다!</span>
        </div>
      )}

      {/* 메인 통합 메뉴바 (스크롤 시 고정) — 공용 컴포넌트 MainTabsNav 사용 */}
      <MainTabsNav className="-mx-6 sm:-mx-10 lg:-mx-16" />

      {/* 구인구직 지역(시/도, 시/군/구) 필터 — 대강/포지션별 서브페이지는 자체 필터 카드를 렌더링하므로 제외 */}
      {view === 'jobs' && (
        <div className="mb-4 flex items-center gap-2">
          <MapPin className="w-4 h-4 text-gray-400 shrink-0" />
          <select
            value={jobProvinceFilter}
            onChange={(e) => { setJobProvinceFilter(e.target.value); setJobDistrictFilter(''); }}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white focus:ring-2 focus:ring-primary focus:border-primary"
          >
            <option value="">전체 지역</option>
            {getAllProvinces().map((province) => (
              <option key={province} value={province}>{province}</option>
            ))}
          </select>
          <select
            value={jobDistrictFilter}
            onChange={(e) => setJobDistrictFilter(e.target.value)}
            disabled={!jobProvinceFilter}
            className={`px-3 py-2 border rounded-lg text-sm font-medium bg-white focus:ring-2 focus:ring-primary focus:border-primary ${
              !jobProvinceFilter ? 'border-gray-200 text-gray-400 cursor-not-allowed' : 'border-gray-300 text-gray-700'
            }`}
          >
            <option value="">전체 시/군/구</option>
            {jobProvinceFilter && getDistricts(jobProvinceFilter).map((district) => (
              <option key={district} value={district}>{district}</option>
            ))}
          </select>
          {jobProvinceFilter && (
            <button
              type="button"
              onClick={() => { setJobProvinceFilter(''); setJobDistrictFilter(''); }}
              className="text-xs text-gray-500 hover:text-gray-700 underline"
            >
              필터 해제
            </button>
          )}
        </div>
      )}

      {/* 레벨 뱃지 탭 — 레벨 뱃지 쇼케이스(원본 HTML 재현 + 애니메이션) */}
      {view === 'classes' && classTab === 'badge' && <DigitalBadge />}

      {/* 인증서 보관함 (디지털 월렛) 탭 — 다크 CTA 밴드부터 보관함까지 동일한 다크 배경 연속 */}
      {view === 'classes' && classTab === 'wallet' && (
        <WalletIntro>
          <CertificateWallet dark />
        </WalletIntro>
      )}

      {/* 카테고리 탭 (자격증 / 온라인 수업 / 수료증 / 워크샵) */}
      {view === 'classes' && classTab === 'certificate' && (
        <ClassCategoryView
          title="자격 과정"
          classes={categoryClasses.certificate}
          categoryTree={categoryTree}
          premiumProviderIds={premiumProviderIds}
          onInstructorClick={handleInstructorClick}
          loading={loadingClasses}
          currentUser={user}
        />
      )}
      {view === 'classes' && classTab === 'online' && (
        <ClassCategoryView
          title="온라인 교육 (100%)"
          classes={categoryClasses.online}
          categoryTree={categoryTree}
          premiumProviderIds={premiumProviderIds}
          onInstructorClick={handleInstructorClick}
          loading={loadingClasses}
          currentUser={user}
        />
      )}
      {view === 'classes' && classTab === 'completion' && (
        <ClassCategoryView
          title="수료 과정"
          classes={categoryClasses.completion}
          categoryTree={categoryTree}
          premiumProviderIds={premiumProviderIds}
          onInstructorClick={handleInstructorClick}
          loading={loadingClasses}
          currentUser={user}
        />
      )}
      {view === 'classes' && classTab === 'workshop' && (
        <ClassCategoryView
          title="워크샵 (세미나)"
          classes={categoryClasses.workshop}
          categoryTree={categoryTree}
          premiumProviderIds={premiumProviderIds}
          onInstructorClick={handleInstructorClick}
          loading={loadingClasses}
          currentUser={user}
        />
      )}

      {view === 'classes' && classTab === 'main' && (
        <div className="space-y-8">
          {/* 우수 인증 교육 기관 — 소개 패널 + 4개씩 페이지 넘김 (FeaturedProviders) */}
          {!loadingPremium && filteredPremiumProviders.length > 0 && (
            <FeaturedProviders providers={filteredPremiumProviders} intro={premiumIntro} />
          )}

          {/* 인기 클래스 — 핫(로즈) 패널 (전체 좋아요 기준 상위 8개) */}
          {!loadingClasses && (
            <PopularClassGrid
              classes={businessFilteredClasses}
              premiumProviderIds={premiumProviderIds}
              onInstructorClick={handleInstructorClick}
              onViewAll={() => document.getElementById('home-all-courses')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            />
          )}


          {/* 전체 교육 과정 — 목업 패널 스타일 */}
          <section
            id="home-all-courses"
            className="space-y-4 rounded-3xl border border-slate-200 p-5 sm:p-8 shadow-sm"
            style={{ background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 55%, #f1f5f9 100%)' }}
          >
          {/* 제목(좌) + 검색·보기방식(우) */}
          <SectionHeading
            eyebrow="나에게 필요한 교육을 검색해보세요"
            title="전체 교육 과정"
            right={
          <div className="flex flex-col sm:flex-row gap-4 flex-1 sm:max-w-[760px] sm:ml-auto p-3 rounded-2xl border border-gray-200 bg-white/90 shadow-sm">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="수업명 검색, 교육기관 검색..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </div>

            {/* Category Filter
            <div className="relative">
              <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors bg-white">
                <span className="text-gray-700">{selectedCategory}</span>
                <ChevronDown className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            */}

            {/* View Mode Toggle */}
            <div className="flex items-center gap-2 border border-gray-300 rounded-lg p-1 bg-white">
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded transition-colors ${
                  viewMode === 'list'
                    ? 'bg-primary text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title="게시판형"
              >
                <List className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('card')}
                className={`p-2 rounded transition-colors ${
                  viewMode === 'card'
                    ? 'bg-primary text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title="카드형"
              >
                <LayoutGrid className="w-5 h-5" />
              </button>
            </div>
          </div>
            }
          />

          {/* 필터 툴바 — 좌: 과정 종류 칩 / 우: 지역·내 기관 보기 (목업 all-items-filter-toolbar) */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-2.5 sm:px-3 rounded-2xl border border-gray-200 bg-white/90 shadow-sm">
            {/* 과정 종류 */}
            <div className="flex items-center gap-2 flex-wrap">
              {([
                { key: 'ALL', label: '전체' },
                { key: 'CERTIFICATE', label: '자격 과정' },
                { key: 'COMPLETION', label: '수료 과정' },
                { key: 'WORKSHOP', label: '워크샵 (세미나)' },
                { key: 'OTHER', label: '기타' },
              ] as const).map((chip) => (
                <button
                  key={chip.key}
                  onClick={() => setClassTypeFilter(chip.key)}
                  className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    classTypeFilter === chip.key
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {chip.label}
                </button>
              ))}
            </div>

            {/* 지역(시/도, 시/군/구) + 내 기관 보기 */}
            <div className="flex items-center gap-2 flex-wrap shrink-0">
              <MapPin className="w-4 h-4 text-gray-400 shrink-0" />
            <select
              value={classProvinceFilter}
              onChange={(e) => { setClassProvinceFilter(e.target.value); setClassDistrictFilter(''); }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white focus:ring-2 focus:ring-primary focus:border-primary"
            >
              <option value="">전체 지역</option>
              {getAllProvinces().map((province) => (
                <option key={province} value={province}>{province}</option>
              ))}
            </select>
            <select
              value={classDistrictFilter}
              onChange={(e) => setClassDistrictFilter(e.target.value)}
              disabled={!classProvinceFilter}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium bg-white focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              <option value="">전체 시/군/구</option>
              {classProvinceFilter && getDistricts(classProvinceFilter).map((district) => (
                <option key={district} value={district}>{district}</option>
              ))}
            </select>
            {classProvinceFilter && (
              <button
                type="button"
                onClick={() => { setClassProvinceFilter(''); setClassDistrictFilter(''); }}
                className="text-xs text-gray-500 hover:text-gray-700 underline"
              >
                필터 해제
              </button>
            )}
            {(user?.role === 'provider' || user?.role === 'admin') && (
              <button
                type="button"
                onClick={() => setOnlyMyInstitutionAllItems((prev) => !prev)}
                className={`ml-auto flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                  onlyMyInstitutionAllItems
                    ? 'bg-primary text-white border-primary'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
              >
                <Building2 className="w-4 h-4" />
                내 기관 보기
              </button>
            )}
            </div>
          </div>

          {/* 진행 상태 탭 */}
          <div className="flex items-center gap-2 border-b border-gray-200 pb-1">
            <button
              onClick={() => setAllItemsStatusFilter('active')}
              className={`px-4 py-2 rounded-t-lg text-sm font-semibold transition-colors border-b-2 -mb-[5px] ${
                allItemsStatusFilter === 'active'
                  ? 'border-primary text-primary bg-white'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              진행중
            </button>
            <button
              onClick={() => setAllItemsStatusFilter('completed')}
              className={`px-4 py-2 rounded-t-lg text-sm font-semibold transition-colors border-b-2 -mb-[5px] ${
                allItemsStatusFilter === 'completed'
                  ? 'border-gray-500 text-gray-700 bg-white'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              종료
            </button>
          </div>

          {/* Classes List */}
          {loadingClasses ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : filteredClasses.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl shadow-soft">
              <p className="text-gray-500">등록된 수업이 없습니다.</p>
            </div>
          ) : viewMode === 'card' ? (
            /* 카드형 뷰 */
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {filteredClasses.map((cls) => (
                <ClassCard
                  key={cls.id}
                  id={cls.id}
                  title={cls.title}
                  providerName={cls.provider_name}
                  thumbnailUrl={cls.thumbnail_url}
                  price={cls.price}
                  discountPrice={cls.discount_price}
                  startDate={cls.start_date}
                  endDate={cls.end_date}
                  location={getLocationText(cls)}
                  locationType={cls.location_type}
                  classType={cls.class_type}
                  status={cls.status}
                  capacity={cls.capacity}
                  currentCount={cls.current_count}
                  closingSoon={cls.closing_soon}
                  isPremium={cls.is_premium || premiumProviders.some(p => p.id === cls.provider_id)}
                  categoryPath={cls.category_path}
                  instructors={cls.instructors}
                  onInstructorClick={handleInstructorClick}
                />
              ))}
            </div>
          ) : (
            /* 게시판형 뷰 (테이블) */
            <div className="bg-white rounded-lg shadow-soft overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        수업명
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        업체명
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        장소
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        기간
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        모집인원
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        수강료
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        등록일
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        상태
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredClasses.map((cls) => {
                      const statusLabel = getStatusLabel(cls.status);
                      return (
                        <tr
                          key={cls.id}
                          className="hover:bg-gray-50 transition-colors cursor-pointer"
                          onClick={() => navigate(`/classes/${cls.id}`)}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-3">
                              {cls.thumbnail_url && (
                                <img
                                  src={cls.thumbnail_url}
                                  alt={cls.title}
                                  className="w-12 h-12 rounded object-cover"
                                />
                              )}
                              <div className="text-sm font-medium text-gray-900 max-w-xs truncate">
                                {cls.title}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-700">{cls.provider_name}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-1 text-sm text-gray-700">
                              <MapPin className="w-4 h-4 text-gray-400" />
                              {getLocationText(cls)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-1 text-sm text-gray-700">
                              <Calendar className="w-4 h-4 text-gray-400" />
                              {/* 과거 수업(가격 0)은 시작일 미입력 → "-" */}
                              {cls.price === 0 ? '-' : new Date(cls.start_date).toLocaleDateString('ko-KR', {
                                month: 'short',
                                day: 'numeric',
                              })}
                              {' ~ '}
                              {new Date(cls.end_date).toLocaleDateString('ko-KR', {
                                month: 'short',
                                day: 'numeric',
                              })}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-1 text-sm text-gray-700">
                              <Users className="w-4 h-4 text-gray-400" />
                              {cls.current_count}/{cls.capacity}명
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-col">
                              <span className="text-sm font-semibold text-primary">
                                ₩{cls.discount_price.toLocaleString()}
                              </span>
                              {cls.price !== cls.discount_price && (
                                <span className="text-xs text-gray-400 line-through">
                                  ₩{cls.price.toLocaleString()}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-700">
                              {new Date(cls.created_at).toLocaleDateString('ko-KR', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                              })}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-1.5">
                              <span
                                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusLabel.color}`}
                              >
                                {statusLabel.text}
                              </span>
                              {cls.closing_soon && (
                                <span className="inline-flex px-2 py-1 text-xs font-bold rounded-full bg-red-500 text-white animate-pulse">
                                  마감 임박
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          </section>
        </div>
      )}

      {/* 구인구직 섹션 */}
      {view === 'jobs' && (
        <div className="space-y-6">
          {/* 종목별 채용 헤더 + 액션 */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 py-1">
            <div>
              <h2 className="text-xl font-bold tracking-tight text-gray-900">종목별 채용</h2>
              <p className="mt-1.5 text-xs text-gray-500">피트니스 전문가를 위한 다양한 채용 정보를 확인하세요.</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {(user?.role === 'employer' || user?.role === 'admin') && (
                <button
                  type="button"
                  onClick={() => navigate('/employer/recruitment?tab=job-postings&new=1')}
                  className="inline-flex items-center justify-center gap-1 px-4 py-2 rounded-lg bg-primary text-white text-xs font-bold hover:bg-primary-600 transition-colors shadow-sm"
                >
                  <Plus className="w-3.5 h-3.5" />
                  새 구인글 등록하기
                </button>
              )}
              <button
                type="button"
                className="inline-flex items-center justify-center gap-1 px-4 py-2 rounded-lg border border-accent/40 bg-accent/5 text-accent-700 text-xs font-bold cursor-default"
              >
                <ArrowUp className="w-3.5 h-3.5" />
                기존 글 끌어올리기
              </button>
              <button
                type="button"
                onClick={() => document.getElementById('board-all')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                className="inline-flex items-center justify-center gap-1 px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 text-xs font-bold hover:bg-gray-50 transition-colors"
              >
                <List className="w-3.5 h-3.5" />
                전체 보기
              </button>
            </div>
          </div>

          {/* 상단: 종목별 채용 바로가기(포지션별 서브페이지) + 채용 서비스 이용 안내 */}
          <div className="grid grid-cols-1 lg:grid-cols-[5fr_3fr] gap-4 items-stretch">
            <div className="grid grid-cols-3 gap-3">
              {POSITION_SUBVIEW_KEYS.map((key) => {
                const cfg = POSITION_SUBVIEWS[key];
                return (
                  <button
                    key={key}
                    onClick={() => setSearchParams({ view: key })}
                    className="min-h-[190px] flex flex-col items-center justify-start gap-0.5 px-3 pt-4 pb-3 rounded-xl border hover:-translate-y-0.5 hover:shadow-md transition-all text-center"
                    style={{ background: `linear-gradient(145deg, ${cfg.bgFrom} 0%, #FFFFFF 72%)`, borderColor: cfg.border }}
                  >
                    <span className="w-[90px] h-[90px] flex items-center justify-center shrink-0" style={{ color: cfg.color }}>
                      <PositionIcon type={cfg.icon} className="w-16 h-16" />
                    </span>
                    <span className="h-5 flex items-center justify-center text-sm font-bold text-gray-800 leading-5">{cfg.label} 채용</span>
                    {cfg.subtext && <span className="text-xs font-medium text-gray-500 leading-4 mt-0">{cfg.subtext}</span>}
                  </button>
                );
              })}
              <div className="min-h-[190px] rounded-xl border border-gray-200 bg-white" aria-hidden="true" />
            </div>

            <JobServiceGuide columns={1} />
          </div>

          {/* 하단: 구인 정보 (전체) + 주변 구직 희망자 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="min-w-0" id="board-all">
              <JobPositionBoard
                title="구인 정보 (전체)"
                jobs={filteredJobPostings}
                loading={loadingJobs}
                onSelectJob={setSelectedJob}
                emptyMessage="등록된 구인 정보가 없습니다."
                footer={(user?.role === 'employer' || user?.role === 'admin') && (
                  <button
                    type="button"
                    onClick={() => navigate('/employer/recruitment?tab=job-postings&new=1')}
                    className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-dashed border-primary/40 text-primary text-sm font-semibold hover:bg-primary/5 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    새 구인 글 등록하기
                  </button>
                )}
              />
            </div>

            {/* 주변 구직 희망자 — 기업 회원/관리자가 아니면 블러 처리 */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
              <SeekerPanel
                seekers={filteredJobSeekers}
                loading={loadingJobSeekers}
                locked={!canViewSeekers}
                onSelectSeeker={handleViewSeeker}
              />
            </div>
          </div>
        </div>
      )}

      {/* 포지션별 채용 서브페이지 (트레이너/필라테스 강사/기타 종목/요가 강사/아카데미 교육 강사) */}
      {POSITION_SUBVIEW_KEYS.includes(view as PositionSubviewKey) && (
        <PositionJobsView
          subviewKey={view as PositionSubviewKey}
          jobs={filteredJobPostings}
          seekers={filteredJobSeekers}
          loading={loadingJobs}
          loadingSeekers={loadingJobSeekers}
          canViewSeekers={canViewSeekers}
          onSelectJob={setSelectedJob}
          onSelectSeeker={handleViewSeeker}
          onBack={() => setSearchParams({ view: 'jobs' })}
          user={user}
          navigate={navigate}
          provinceFilter={jobProvinceFilter}
          districtFilter={jobDistrictFilter}
          onProvinceChange={(v) => { setJobProvinceFilter(v); setJobDistrictFilter(''); }}
          onDistrictChange={setJobDistrictFilter}
        />
      )}

      {/* 대강 (대타 강사) — 대강 포지션 채용만 */}
      {view === 'substitute' && (
        <SubstituteJobsView
          jobs={filteredJobPostings}
          loading={loadingJobs}
          user={user}
          navigate={navigate}
          onSelectJob={setSelectedJob}
          provinceFilter={jobProvinceFilter}
          districtFilter={jobDistrictFilter}
          onProvinceChange={(v) => { setJobProvinceFilter(v); setJobDistrictFilter(''); }}
          onDistrictChange={setJobDistrictFilter}
        />
      )}

      {/* 지식 챌린지 탭 — 원본 HTML 재현 (히어로 + 분야별 챌린지 카드) */}
      {view === 'challenge' && <KnowledgeChallenge />}

      {/* 구인 정보 상세 모달 */}
      {selectedJob && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setSelectedJob(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                {selectedJob.is_premium_employer && <Crown className="w-5 h-5 text-amber-500" />}
                구인 상세
              </h2>
              <button onClick={() => setSelectedJob(null)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900">{selectedJob.title}</h3>
                {selectedJob.company_name && <p className="text-primary font-medium mt-1">{selectedJob.company_name}</p>}
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-gray-50 rounded-lg p-3">
                  <span className="text-gray-500 block text-xs mb-1">포지션</span>
                  <span className="font-medium">{getPositionLabel(selectedJob.position)}</span>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <span className="text-gray-500 block text-xs mb-1">고용형태</span>
                  <span className="font-medium">{{ FULL_TIME: '정규직', CONTRACT: '계약직', PART_TIME: '파트타임', FREELANCE: '프리랜서' }[selectedJob.employment_type as string] || selectedJob.employment_type}</span>
                </div>
                {selectedJob.salary_type && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <span className="text-gray-500 block text-xs mb-1">급여</span>
                    <span className="font-medium">
                      {{ HOURLY: '시급', DAILY: '일급', MONTHLY: '월급', YEARLY: '연봉', NEGOTIABLE: '협의' }[selectedJob.salary_type as string]}
                      {selectedJob.salary_type !== 'NEGOTIABLE' && (selectedJob.salary_min || selectedJob.salary_max) &&
                        ` ${(selectedJob.salary_min || 0).toLocaleString()}~${(selectedJob.salary_max || 0).toLocaleString()}원`}
                    </span>
                  </div>
                )}
                {selectedJob.recruitment_count && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <span className="text-gray-500 block text-xs mb-1">모집인원</span>
                    <span className="font-medium">{selectedJob.recruitment_count}명</span>
                  </div>
                )}
                {selectedJob.work_hours && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <span className="text-gray-500 block text-xs mb-1">근무시간</span>
                    <span className="font-medium">{selectedJob.work_hours}</span>
                  </div>
                )}
                {selectedJob.deadline && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <span className="text-gray-500 block text-xs mb-1">마감일</span>
                    <span className="font-medium">{selectedJob.deadline}</span>
                  </div>
                )}
              </div>
              {(selectedJob.work_location || selectedJob.employer_address) && (
                <div>
                  <div className="flex items-start gap-2 text-sm">
                    <MapPin className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                    <span className="text-gray-700">{selectedJob.work_location || selectedJob.employer_address}</span>
                  </div>
                  {((selectedJob.work_location_lat && selectedJob.work_location_lng) || (selectedJob.employer_lat && selectedJob.employer_lng)) && (
                    <iframe
                      width="100%"
                      height="220"
                      style={{ border: 0 }}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      src={`https://www.google.com/maps/embed/v1/place?key=${GOOGLE_MAPS_API_KEY}&q=${selectedJob.work_location_lat || selectedJob.employer_lat},${selectedJob.work_location_lng || selectedJob.employer_lng}&zoom=15&language=ko`}
                      className="rounded-lg mt-2"
                    />
                  )}
                </div>
              )}
              {selectedJob.description && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">상세 설명</h4>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="prose prose-sm max-w-none text-gray-700" dangerouslySetInnerHTML={{ __html: selectedJob.description }} />
                  </div>
                </div>
              )}
              {selectedJob.requirements && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">자격 요건</h4>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedJob.requirements}</p>
                </div>
              )}
              {selectedJob.preferred_skills && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">우대 사항</h4>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedJob.preferred_skills}</p>
                </div>
              )}
              {selectedJob.contact_name && (
                <div className="border-t pt-3 text-sm">
                  <h4 className="font-semibold text-gray-900 mb-1">담당자</h4>
                  <p className="text-gray-700">
                    {selectedJob.contact_name}
                    {selectedJob.contact_phone && ` · ${selectedJob.contact_phone}`}
                    {selectedJob.contact_email && ` · ${selectedJob.contact_email}`}
                  </p>
                </div>
              )}

              {/* 지원하기 버튼 — 일반 회원(비로그인 포함) 전용 */}
              {(!user || user.role === 'user') && (
                <div className="border-t pt-4 flex flex-col items-stretch gap-2">
                  {appliedJobIds.has(selectedJob.id) ? (
                    <button
                      disabled
                      className="w-full py-3 bg-gray-100 text-gray-500 rounded-lg font-medium cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      <CheckCircle className="w-5 h-5" />
                      지원 완료
                    </button>
                  ) : (
                    <button
                      onClick={() => handleApplyJob(selectedJob)}
                      disabled={applyingJob || selectedJob.status !== 'OPEN'}
                      className="w-full py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      <Briefcase className="w-5 h-5" />
                      {applyingJob ? '지원 중...' : selectedJob.status !== 'OPEN' ? '모집 마감' : '지원하기'}
                    </button>
                  )}
                  {!user && (
                    <p className="text-xs text-gray-500 text-center">로그인 후 지원할 수 있습니다.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 구직자 상세 모달 */}
      {selectedSeeker && (() => {
        const tpl = (seekerDetail?.resume_template || 1) as 1 | 2 | 3;
        const modalW = tpl === 3 ? 1210 : 858;
        const seekerUser = seekerDetail ? {
          id: seekerDetail.user_id,
          email: seekerDetail.user_email || '',
          role: 'user' as const,
          name: seekerDetail.user_name || '',
          name_en: seekerDetail.user_name_en || '',
          birth_date: seekerDetail.user_birth_date || '',
          phone: seekerDetail.user_phone || '',
        } : null;
        const getSeekerPortfolio = (slot: number): string =>
          (seekerDetail?.portfolio_images || [])[slot] || '';
        const visibility = seekerDetail?.visibility || {
          education: true, experience: true, awards: true,
          certificates_list: true, introduction: true, stampfit_certificates: true, portfolio: true,
        };
        const TplComponent = tpl === 3 ? ResumeTemplate3Preview : tpl === 2 ? ResumeTemplate2Preview : ResumeTemplate1Preview;
        return (
          <div className="fixed inset-0 z-50 bg-black/65 flex items-center justify-center p-4" onClick={() => { setSelectedSeeker(null); setSeekerDetail(null); setLightboxUrl(null); }}>
            <div
              className="rounded-2xl shadow-2xl bg-gray-100 flex flex-col overflow-hidden"
              style={{ width: modalW, maxWidth: 'calc(100vw - 32px)', height: 'calc(100vh - 32px)', minWidth: 0 }}
              onClick={e => e.stopPropagation()}
            >
              {/* 헤더 */}
              <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-2 flex items-center justify-between">
                <h2 className="text-sm font-bold text-gray-900 flex items-center gap-1.5">
                  <User className="w-4 h-4 text-primary" />
                  구직자 이력서
                  <span className="text-xs font-normal text-gray-400">스타일 {tpl}</span>
                </h2>
                <button onClick={() => { setSelectedSeeker(null); setSeekerDetail(null); setLightboxUrl(null); }} className="text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
              </div>

              {/* 내용 */}
              <div className="flex-1 min-h-0 overflow-hidden relative">
                {loadingSeekerDetail ? (
                  <div className="flex items-center justify-center h-full text-gray-500">로딩 중...</div>
                ) : seekerDetail ? (
                  <ResumeModalScaler
                    pageW={tpl === 3 ? 1123 : 794}
                    pageH={tpl === 3 ? 794 : 1123}
                  >
                    <TplComponent
                      user={seekerUser as any}
                      photoUrl={seekerDetail.photo_url || null}
                      education={seekerDetail.education || []}
                      experience={seekerDetail.experience || []}
                      awards={seekerDetail.awards || []}
                      certificatesList={seekerDetail.certificates_list || []}
                      stampfitCerts={seekerDetail.stampfit_certificates || []}
                      introduction={seekerDetail.introduction || ''}
                      visibility={visibility}
                      desiredPosition={seekerDetail.desired_position || ''}
                      address={seekerDetail.address || ''}
                      getPortfolioDisplayUrl={getSeekerPortfolio}
                      {...(tpl === 2 ? { onPhotoClick: (url: string) => setLightboxUrl(url) } : {})}
                    />
                  </ResumeModalScaler>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-500">프로필을 불러올 수 없습니다.</div>
                )}
              </div>

              {/* 메시지 보내기 푸터 */}
              <div className="flex-shrink-0 bg-gray-50 border-t border-gray-200 px-4 py-2 flex items-center justify-end">
                {user?.id === selectedSeeker.user_id ? (
                  <span className="text-xs text-gray-400">본인 이력서입니다</span>
                ) : (
                  <StartChatButton targetUserId={selectedSeeker.user_id} />
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* 포트폴리오 라이트박스 */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-[200] bg-black/85 flex items-center justify-center p-4"
          onClick={() => setLightboxUrl(null)}
        >
          <button
            onClick={() => setLightboxUrl(null)}
            className="absolute top-4 right-4 text-white text-3xl leading-none hover:text-gray-300"
          >&times;</button>
          <img
            src={lightboxUrl}
            alt="포트폴리오 확대"
            style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain', display: 'block' }}
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}

      {/* 강사 상세 모달 */}
      <InstructorDetailsModal
        isOpen={showInstructorModal}
        instructor={selectedInstructor}
        onClose={() => {
          setShowInstructorModal(false);
          setSelectedInstructor(null);
        }}
      />
    </div>
  );
}
